import React from 'react';
import { Select, MenuItem, FormControl, useTheme, CircularProgress, Box } from '@mui/material';
import { FONT_PRIMARY } from '../../theme/typography';
import useGetAvailableModels, { LLMModel } from '../../hooks/KnowledgeAI/useGetAvailableModels';

type ModelSelectionDropdownProps = {
  /** Currently selected model name string (e.g. "gpt-4.1"). */
  selectedModel: string;
  /** Called when user picks a model. Passes the model_name string. */
  onModelChange: (modelName: string) => void;
};

const ModelSelectionDropdown = ({ selectedModel, onModelChange }: ModelSelectionDropdownProps) => {
  const theme = useTheme();
  const { data: models, loading } = useGetAvailableModels();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (!models || models.length === 0) return null;

  const isValidSelection = models.some((m: LLMModel) => m.model_name === selectedModel);
  const effectiveValue = isValidSelection ? selectedModel : models[0].model_name;

  return (
    <FormControl
      size="small"
      variant="filled"
      sx={{
        '& .MuiInputBase-root': { fontSize: '0.8125rem', padding: '0px' },
      }}
    >
      <Select
        value={effectiveValue}
        onChange={(e) => onModelChange(e.target.value as string)}
        displayEmpty
        renderValue={(v) => (v ? String(v) : 'Model')}
        sx={{
          '&:before, &:after': { display: 'none' },
          '& .MuiSelect-select': { p: 1 },
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
          borderRadius: '8px',
          fontFamily: FONT_PRIMARY,
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'top', horizontal: 'left' },
          transformOrigin: { vertical: 'bottom', horizontal: 'left' },
          PaperProps: {
            sx: {
              fontFamily: FONT_PRIMARY,
              '& .MuiMenuItem-root': {
                fontFamily: FONT_PRIMARY,
              },
            },
          },
        }}
      >
        {models.map((model: LLMModel) => (
          <MenuItem key={model.id} value={model.model_name} dense sx={{ fontFamily: FONT_PRIMARY }}>
            {model.model_name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ModelSelectionDropdown;
